import type { NearestMinutes } from "@@/shared/nearest-minutes";
import { getTimeOfDay } from "@@/utilities/date-and-time/time-of-day";
import { z } from "zod";
import type { IsoAndUnixTimestamp } from "../date-time/iso-and-unix";
import { isIsoAndUnix } from "../date-time/iso-and-unix";
import type {
    TimeRange} from "./time-range";
import {
    isTimeRange,
    timeRangeToString,
    timeRangeToStringRounded,
    timeRangeZodSchema,
} from "./time-range";
import type {
    TimeSpot} from "./time-spot";
import {
    isTimeSpot,
    timeSpotToString,
    timeSpotToStringRounded,
    timeSpotZodSchema,
} from "./time-spot";

type Time = TimeSpot | TimeRange;
const timeZodSchema: z.ZodSchema<Time> = z.union([
    timeSpotZodSchema,
    timeRangeZodSchema,
]);

const getStart = (time: Time): IsoAndUnixTimestamp => time.start;
const timeToString = (
    time: Time,
    options?: {
        onlyStartTime?: boolean;
    },
) => {
    if (isTimeRange(time)) return timeRangeToString(time, options);

    if (isTimeSpot(time)) {
        return timeSpotToString(time);
    }

    if (isIsoAndUnix(time)) {
        return getTimeOfDay(time);
    }

    throw new Error("Invalid time format; timeToString");
};

const timeToStringRounded = (
    time: Time,
    roundedToNearestMinute: NearestMinutes,
    shortenMinutes = false,
    onlyStartTime = false,
) =>
    time._type === "TIME_SPOT" || onlyStartTime
        ? timeSpotToStringRounded(time, roundedToNearestMinute)
        : timeRangeToStringRounded(
              time,
              roundedToNearestMinute,
              shortenMinutes,
          );

export { getStart, timeToString, timeToStringRounded, timeZodSchema };
export type { Time };
